import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { gql } from '@soundxyz/gql-string';
import { keyframes, styled } from '@components-shared/src/stitches';
import { HERO_COVER_MOBILE_SIZE, HERO_COVER_SIZE } from '@components/basics/Hero';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, SongHeroCoverImagesFragmentDoc } from '@graphql/reactQuery';
import { getAnimatedGoldenEggImage } from '@utils/helpers';
import { SongCoverImage } from './CoverImage';
import { GoldenEggCoverBadge } from './GoldenEggCoverBadge';
import { CARD_VARIANTS_DESKTOP_NEW, CARD_VARIANTS_MOBILE_NEW } from './constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SongHeroCoverImages on Release {
    id
    artist {
      id
      name
      webappUri
    }
    title
    coverImage {
      id
      url
    }
    animatedCoverImage {
      id
      url
    }
    goldenEggImage {
      id
      url
    }
    animatedGoldenEggImageOptimized {
      id
      url
    }
    ...GoldenEggCoverBadge
  }
`);

const GOLDEN_EGG_BORDER_TOTAL_SIZE = 12;

export const SongHeroCoverImages = ({
  showGoldenEggToggle,
  hideCountdown,
  release,
}: {
  showGoldenEggToggle: boolean;
  hideCountdown: boolean;
  release: FragmentType<SongHeroCoverImagesFragmentDoc>;
}) => {
  const { tablet } = useMediaQuery();
  const [toggled, setToggled] = useState(false);
  const [wrapperHover, setWrapperHover] = useState(false);
  const [initial, setInitial] = useState(true);
  const coverSize = tablet ? HERO_COVER_SIZE : HERO_COVER_MOBILE_SIZE;

  const releaseData = getFragment(SongHeroCoverImagesFragmentDoc, release);

  const duration = {
    duration: 0.6,
  };

  const spring = {
    ...duration,
    type: 'spring',
  };

  const cardVariants = tablet ? CARD_VARIANTS_DESKTOP_NEW : CARD_VARIANTS_MOBILE_NEW;

  return (
    <>
      {showGoldenEggToggle && (
        <CardsWrapper
          onClick={() => {
            setToggled(!toggled);
            setInitial(false);
          }}
          onMouseOver={() => setWrapperHover(true)}
          onMouseLeave={() => setWrapperHover(false)}
          isToggled={toggled}
        >
          <DefaultCover
            variants={cardVariants}
            animate={initial ? 'initial' : toggled ? 'toggled' : 'default'}
            transition={duration}
            // Prevent scroll bar from appearing when animation occurs
            onAnimationStart={() => {
              document.body.classList.add('overflow-x-hidden');
            }}
            onAnimationComplete={() => {
              document.body.classList.remove('overflow-x-hidden');
            }}
          >
            <motion.div
              className="song-card"
              animate={{ x: tablet ? (wrapperHover ? 0 : toggled ? 16 : -16) : 0 }}
              transition={spring}
            >
              <SongCoverImage
                songTitle={releaseData.title}
                isAnimated={!!releaseData.animatedCoverImage}
                coverImage={releaseData.animatedCoverImage?.url || releaseData.coverImage.url}
                size={coverSize}
                hasCountdown={!hideCountdown}
                showGoldenEggToggle
              />
            </motion.div>
          </DefaultCover>
          <GoldenEggCover
            variants={cardVariants}
            animate={initial ? 'initialEgg' : toggled ? 'toggledEgg' : 'defaultEgg'}
            transition={duration}
          >
            <motion.div
              className="golden-egg-card"
              animate={{ x: tablet ? (wrapperHover ? 0 : toggled ? -16 : 16) : 0 }}
              transition={spring}
            >
              <ImageWrapper>
                <BadgeContainer>
                  <GoldenEggCoverBadge releaseFrag={releaseData} />
                </BadgeContainer>
                <SongCoverImage
                  songTitle={releaseData.title}
                  isAnimated={!!releaseData.animatedGoldenEggImageOptimized}
                  coverImage={getAnimatedGoldenEggImage({
                    goldenEggImage: releaseData.goldenEggImage.url,
                    animatedGoldenEggImage: releaseData.animatedGoldenEggImageOptimized?.url,
                  })}
                  size={coverSize - (tablet ? 0 : 12)}
                  hasCountdown={!hideCountdown}
                  showGoldenEggToggle
                />
              </ImageWrapper>
            </motion.div>
          </GoldenEggCover>
        </CardsWrapper>
      )}
      {!showGoldenEggToggle && (
        <SongCoverImage
          songTitle={releaseData.title}
          isAnimated={!!releaseData.animatedCoverImage}
          coverImage={releaseData.animatedCoverImage?.url || releaseData.coverImage.url}
          size={coverSize}
          hasCountdown={!hideCountdown}
          showGoldenEggToggle={false}
        />
      )}
    </>
  );
};

const decreaseOpacity = keyframes({
  '40%': {
    opacity: 1,
  },
  '60%': {
    opacity: 0.5,
  },
  '100%': {
    opacity: 0.5,
  },
});

const increaseOpacity = keyframes({
  '0%': {
    opacity: 0.5,
  },
  '50%': {
    opacity: 1,
  },
});

const CardsWrapper = styled('div', {
  position: 'relative',
  width: HERO_COVER_MOBILE_SIZE,
  height: HERO_COVER_MOBILE_SIZE,
  perspective: 1200,
  zIndex: '$above2',
  /* the line below is super important - indicates that the children of
  the element should be positioned in the 3D-space. */
  transformStyle: 'preserve-3d',
  marginBottom: 24,

  '@tablet': {
    width: HERO_COVER_SIZE,
    height: HERO_COVER_SIZE,
  },

  '.song-card, .golden-egg-card': {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: HERO_COVER_MOBILE_SIZE,
    height: HERO_COVER_MOBILE_SIZE,
    borderRadius: 8,
    userSelect: 'none',
    '@tablet': {
      width: HERO_COVER_SIZE + 12,
      height: HERO_COVER_SIZE + 12,
    },
  },

  variants: {
    isToggled: {
      true: {
        '.song-card': {
          animation: `1s ease 0s normal forwards 1 ${decreaseOpacity}`,
        },
        '.golden-egg-card': {
          animation: `1s ease 0s normal forwards 1 ${increaseOpacity}`,
        },
      },
      false: {
        '.song-card': {
          animation: `1s ease 0s normal forwards 1 ${increaseOpacity}`,
        },
        '.golden-egg-card': {
          animation: `1s ease 0s normal forwards 1 ${decreaseOpacity}`,
        },
      },
    },
  },
});

const GoldenEggCover = styled(motion.div, {
  position: 'relative',
});

const DefaultCover = styled(motion.div, {
  position: 'relative',
});

const BadgeContainer = styled('div', {
  position: 'absolute',
  zIndex: '$above1',
  top: 16,
  left: 16,
});

const ImageWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  backgroundImage: `url('/images/golden-egg-image-border.svg')`,
  backgroundSize: 'cover',
  width: HERO_COVER_MOBILE_SIZE,
  height: HERO_COVER_MOBILE_SIZE,
  borderRadius: 14,
  alignItems: 'center',
  '@tablet': {
    width: HERO_COVER_SIZE + GOLDEN_EGG_BORDER_TOTAL_SIZE,
    height: HERO_COVER_SIZE + GOLDEN_EGG_BORDER_TOTAL_SIZE,
  },
});
